/**
 * resultDrawerNav.ts
 *
 * URL helpers for the Result drawer return-link origin tracking feature (mr-3j4t.1).
 *
 * These helpers are pure functions with no React/router dependencies so they
 * can be tested in isolation without rendering.
 *
 * Origin tracking: when the user opens the Result drawer via ResultItem3.handleInfo,
 * the current tab (and interval, if on Status) are captured as ?from / ?fromInterval
 * query params. On back navigation, handleBackToObjective reads these params via
 * getValidReturnTab and restores the correct tab+interval. On drawer close, both
 * params are stripped by handleClose.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Query param name for origin tab. */
export const PARAM_FROM = 'from';

/** Query param name for origin interval (only set when from=status). */
export const PARAM_FROM_INTERVAL = 'fromInterval';

/**
 * Enumerated set of valid tab values for the `?from` param.
 * These mirror the DrawerTabBar tab IDs for the objective context.
 * Note: `result` is intentionally excluded — a Result cannot originate from
 * another Result; pager transitions within a Result preserve the original `from`.
 */
export const VALID_FROM_TABS = [
  'info',
  'status',
  'results',
  'comments',
  'settings',
  'team',
  'descendants',
] as const;

export type ValidFromTab = (typeof VALID_FROM_TABS)[number];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReturnTabResult = {
  tab: string;
  forInterval?: string;
};

type GetValidReturnTabArgs = {
  from: string | null | undefined;
  fromInterval: string | null | undefined;
};

type GetReturnLinkAriaLabelArgs = {
  from: string | null | undefined;
  fromInterval: string | null | undefined;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the given string is a valid ISO date string (YYYY-MM-DD).
 * Used to validate ?fromInterval before restoring it as ?forInterval.
 */
function isValidISODate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * Returns the tab and optional forInterval to navigate to when the user clicks
 * the back button in the Result drawer.
 *
 * Edge cases:
 * 1. Missing `from` (direct deep-link) → fall back to tab=info
 * 2. Unknown `from` value (typo, stale bookmark) → fall back to tab=info (dev warning)
 * 3. `fromInterval` out of range or malformed → set tab=status, drop forInterval
 * 4. `from=status` with no `fromInterval` → set tab=status, no forInterval (valid)
 */
export function getValidReturnTab({ from, fromInterval }: GetValidReturnTabArgs): ReturnTabResult {
  // Edge case 1: missing from — fallback
  if (!from) {
    return { tab: 'info' };
  }

  // Edge case 2: unknown from value — fallback with dev warning
  if (!(VALID_FROM_TABS as readonly string[]).includes(from)) {
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      console.warn(`[resultDrawerNav] Unknown ?from value: "${from}". Falling back to tab=info.`);
    }
    return { tab: 'info' };
  }

  const tab = from;

  // Only restore interval when from=status
  if (tab === 'status' && fromInterval) {
    // Edge cases 3 & 4: validate the interval string
    if (isValidISODate(fromInterval)) {
      return { tab, forInterval: fromInterval };
    }
    // Malformed or out-of-range — set status tab but drop interval
    return { tab };
  }

  return { tab };
}

/**
 * Composes an accessible aria-label for the Result drawer back button.
 *
 * Examples:
 * - from=status, fromInterval=2026-05-31 → "Back to objective status at May 2026"
 * - from=info                            → "Back to objective info"
 * - from=descendants                     → "Back to objective descendants"
 * - no from                              → "Back to objective info"
 */
export function getReturnLinkAriaLabel({
  from,
  fromInterval,
}: GetReturnLinkAriaLabelArgs): string {
  const { tab, forInterval } = getValidReturnTab({ from, fromInterval });

  if (tab === 'status' && forInterval) {
    // Format: "Back to objective status at <Month YYYY>"
    const date = new Date(forInterval + 'T00:00:00'); // force local date parse
    const formatted = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return `Back to objective status at ${formatted}`;
  }

  return `Back to objective ${tab}`;
}
